/* eslint-disable react/prop-types */
import { useState, useEffect, useRef, useMemo } from "react";
import API, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  TextB, TextItalic, TextUnderline, ListBullets, ListNumbers,
  LinkSimple, Paperclip, FileText, X, ArrowSquareOut, AddressBook, Check,
  ArrowsOut, ArrowsIn,
} from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Email composer used for both NEW emails and REPLIES on the case
 * Communications tab.
 *
 * Props:
 *   caseId      – the case to send from / record the email against
 *   open        – dialog visibility
 *   onClose()   – close handler
 *   onSent()    – fires after the row is recorded so the parent can refresh
 *   replyTo     – optional original message: {id, from_email, from_name,
 *                 to_emails, cc_emails, subject, body_html, sent_at}
 *   prefillClients – array of {email, name} to default into the To field
 *                 when composing fresh
 *
 * Rich text is captured via a contentEditable div + a minimal toolbar that
 * uses document.execCommand. execCommand is technically deprecated but
 * remains universally supported and avoids pulling in a 100KB editor lib
 * for what is essentially a 4-button toolbar.
 */
export default function ComposeEmailDialog({ caseId, open, onClose, onSent, replyTo, mode = "new", prefillClients, propertyAddress = "", addressBook = [] }) {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [maximized, setMaximized] = useState(false);
  const [attachDocIds, setAttachDocIds] = useState([]);
  const [uploads, setUploads] = useState([]);  // [{filename, content_type, content_b64, size}]
  const [caseDocs, setCaseDocs] = useState([]);
  const [caseFolders, setCaseFolders] = useState([]);
  const [docFilter, setDocFilter] = useState("");
  const [sending, setSending] = useState(false);
  const [showDocPicker, setShowDocPicker] = useState(false);
  const bodyRef = useRef(null);
  const fileInputRef = useRef(null);

  // Reset / prefill whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    const fillReply = (allMode) => {
      const senderEmail = replyTo.from_email || "";
      // For reply-all we Cc everyone originally on To + Cc, minus the
      // current sender and minus the original sender (already in To).
      const myEmail = (prefillClients || []).find((c) => c.is_self)?.email || "";
      const ccPool = [
        ...(replyTo.to_emails || []),
        ...(replyTo.cc_emails || []),
      ].map((e) => (e || "").toLowerCase()).filter(Boolean);
      const uniqCc = [...new Set(ccPool)].filter((e) => e !== senderEmail.toLowerCase() && e !== myEmail.toLowerCase());
      setTo(senderEmail);
      setCc(allMode ? uniqCc.join(", ") : "");
      const subj = (replyTo.subject || "").trim();
      setSubject(subj.toLowerCase().startsWith("re:") ? subj : `Re: ${subj}`);
      const when = replyTo.sent_at ? new Date(replyTo.sent_at).toLocaleString("en-GB") : "";
      const senderLabel = replyTo.from_name ? `${replyTo.from_name} <${senderEmail}>` : senderEmail;
      const quoted = `
        <p><br/></p>
        <p>—</p>
        <p style="color:#64748b;font-size:12px;">On ${when}, ${senderLabel} wrote:</p>
        <blockquote style="border-left:3px solid #cbd5e1;padding-left:12px;color:#475569;">
          ${replyTo.body_html || replyTo.body_preview || ""}
        </blockquote>
      `;
      setTimeout(() => { if (bodyRef.current) bodyRef.current.innerHTML = quoted; }, 30);
    };
    if (mode === "reply" && replyTo) {
      fillReply(false);
    } else if (mode === "replyAll" && replyTo) {
      fillReply(true);
    } else if (mode === "forward" && replyTo) {
      setTo("");
      setCc("");
      const subj = (replyTo.subject || "").trim();
      setSubject(subj.toLowerCase().startsWith("fwd:") || subj.toLowerCase().startsWith("fw:") ? subj : `Fwd: ${subj}`);
      const when = replyTo.sent_at ? new Date(replyTo.sent_at).toLocaleString("en-GB") : "";
      const senderLabel = replyTo.from_name ? `${replyTo.from_name} <${replyTo.from_email}>` : replyTo.from_email;
      const toRecipients = (replyTo.to_emails || []).join(", ");
      const ccRecipients = (replyTo.cc_emails || []).join(", ");
      const forwarded = `
        <p><br/></p>
        <p>—</p>
        <p style="color:#64748b;font-size:12px;"><strong>---------- Forwarded message ----------</strong><br/>
          <strong>From:</strong> ${senderLabel}<br/>
          <strong>Date:</strong> ${when}<br/>
          <strong>Subject:</strong> ${replyTo.subject || ""}<br/>
          <strong>To:</strong> ${toRecipients}${ccRecipients ? `<br/><strong>Cc:</strong> ${ccRecipients}` : ""}
        </p>
        <div>${replyTo.body_html || replyTo.body_preview || ""}</div>
      `;
      setTimeout(() => { if (bodyRef.current) bodyRef.current.innerHTML = forwarded; }, 30);
    } else {
      setTo((prefillClients || []).map((c) => c.email).filter(Boolean).join(", "));
      setCc("");
      // For new emails from a case file we default the subject to the
      // property address. Solicitors can edit it before sending; saves
      // typing the address out every time and tags the thread visually.
      setSubject((propertyAddress || "").trim());
      setTimeout(() => { if (bodyRef.current) bodyRef.current.innerHTML = ""; }, 30);
    }
    setAttachDocIds([]);
    setUploads([]);
    setShowDocPicker(false);
  }, [open, replyTo, mode, prefillClients, propertyAddress]);

  // Lazy-fetch case docs + folders the first time the user opens the doc
  // picker. Folders let us group docs (Onboarding, Mortgage, Comp Docs…)
  // so a chatty case file stays scannable.
  useEffect(() => {
    if (!caseId || !showDocPicker || caseDocs.length > 0) return;
    (async () => {
      try {
        const [docsResp, foldersResp] = await Promise.all([
          API.get(`/cases/${caseId}/documents`),
          API.get(`/cases/${caseId}/folders`).catch(() => ({ data: [] })),
        ]);
        setCaseDocs(Array.isArray(docsResp.data) ? docsResp.data : []);
        setCaseFolders(Array.isArray(foldersResp.data) ? foldersResp.data : []);
      } catch {
        setCaseDocs([]);
        setCaseFolders([]);
      }
    })();
  }, [showDocPicker, caseId, caseDocs.length]);

  const exec = (cmd, value) => {
    if (bodyRef.current) bodyRef.current.focus();
    document.execCommand(cmd, false, value);
  };
  const handleLink = () => {
    const url = window.prompt("Link URL");
    if (url) exec("createLink", url);
  };

  const handleFiles = async (files) => {
    const arr = Array.from(files || []);
    const next = [...uploads];
    for (const f of arr) {
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`${f.name} is over 10MB — skipped`);
        continue;
      }
      // Read as data URL → split off the base64 portion.
      // eslint-disable-next-line no-await-in-loop
      const b64 = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = String(reader.result || "");
          const idx = dataUrl.indexOf(",");
          res(idx >= 0 ? dataUrl.slice(idx + 1) : "");
        };
        reader.onerror = rej;
        reader.readAsDataURL(f);
      });
      next.push({
        filename: f.name,
        content_type: f.type || "application/octet-stream",
        content_b64: b64,
        size: f.size,
      });
    }
    setUploads(next);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const splitEmails = (s) =>
    (s || "")
      .split(/[,;\n]/)
      .map((e) => e.trim())
      .filter(Boolean);

  const handleSend = async () => {
    const toArr = splitEmails(to);
    const ccArr = splitEmails(cc);
    if (toArr.length === 0) {
      toast.error("Add at least one recipient");
      return;
    }
    if (!subject.trim()) {
      toast.error("Subject is required");
      return;
    }
    const html = (bodyRef.current?.innerHTML || "").trim();
    if (!html || html === "<br>" || html === "<div><br></div>") {
      toast.error("Body cannot be empty");
      return;
    }
    setSending(true);
    try {
      // When `caseId` is provided we record the email on that case; for
      // ad-hoc replies fired from the Inbox triage page we use a thinner
      // endpoint that just dispatches via Outlook and skips the case
      // attach + records.
      const url = caseId
        ? `/cases/${caseId}/communications/send`
        : `/outlook/send`;
      const body = {
        to: toArr,
        cc: ccArr,
        subject: subject.trim(),
        html,
        attach_uploads: uploads,
      };
      if (caseId) {
        body.attach_case_doc_ids = attachDocIds;
        body.reply_to_comm_id = (mode === "reply" || mode === "replyAll") ? (replyTo?.id || null) : null;
      }
      const { data } = await API.post(url, body);
      const where = data.mode === "outlook" ? " via your Outlook" : "";
      toast.success(`Email sent${where} to ${toArr.length} recipient${toArr.length === 1 ? "" : "s"}`);
      onSent?.();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const totalUploadKb = useMemo(
    () => uploads.reduce((acc, u) => acc + (u.size || 0), 0) / 1024,
    [uploads],
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !sending && onClose()}>
      <DialogContent
        className={maximized
          ? "w-[98vw] max-w-[98vw] h-[96vh] max-h-[96vh] overflow-y-auto"
          : "max-w-2xl max-h-[92vh] overflow-y-auto"}
        data-testid="compose-email-dialog"
      >
        <DialogHeader>
          <div className="flex items-center justify-between gap-2 pr-6">
            <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>
              {mode === "reply" && "Reply"}
              {mode === "replyAll" && "Reply all"}
              {mode === "forward" && "Forward"}
              {mode === "new" && "New email"}
            </DialogTitle>
            <button
              type="button"
              onClick={() => setMaximized((m) => !m)}
              className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100 text-slate-500"
              title={maximized ? "Restore" : "Maximize"}
              data-testid="compose-maximize-toggle"
            >
              {maximized ? <ArrowsIn weight="bold" className="w-4 h-4" /> : <ArrowsOut weight="bold" className="w-4 h-4" />}
            </button>
          </div>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="compose-to" className="text-xs font-medium">To</Label>
            <div className="flex items-stretch gap-1.5">
              <Input
                id="compose-to"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="alice@example.com, bob@example.com"
                className="h-9 text-sm flex-1"
                data-testid="compose-to"
              />
              <AddressPicker
                book={addressBook}
                current={to}
                onChange={setTo}
                testId="compose-to-picker"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="compose-cc" className="text-xs font-medium">Cc (optional)</Label>
            <div className="flex items-stretch gap-1.5">
              <Input
                id="compose-cc"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                placeholder="(optional)"
                className="h-9 text-sm flex-1"
                data-testid="compose-cc"
              />
              <AddressPicker
                book={addressBook}
                current={cc}
                onChange={setCc}
                testId="compose-cc-picker"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="compose-subject" className="text-xs font-medium">Subject</Label>
            <Input
              id="compose-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="h-9 text-sm"
              data-testid="compose-subject"
              maxLength={250}
            />
          </div>

          {/* Rich-text editor */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Message</Label>
            <div className="border border-slate-200 rounded-md focus-within:border-slate-400 focus-within:ring-1 focus-within:ring-slate-300">
              <div className="flex items-center gap-0.5 px-2 py-1 border-b border-slate-200 bg-slate-50 rounded-t-md flex-wrap" data-testid="compose-toolbar">
                <ToolbarButton onClick={() => exec("bold")} label="Bold" testId="compose-bold"><TextB weight="bold" className="w-3.5 h-3.5" /></ToolbarButton>
                <ToolbarButton onClick={() => exec("italic")} label="Italic" testId="compose-italic"><TextItalic weight="bold" className="w-3.5 h-3.5" /></ToolbarButton>
                <ToolbarButton onClick={() => exec("underline")} label="Underline" testId="compose-underline"><TextUnderline weight="bold" className="w-3.5 h-3.5" /></ToolbarButton>
                <span className="w-px h-4 bg-slate-200 mx-1" />
                <ToolbarButton onClick={() => exec("insertUnorderedList")} label="Bulleted list" testId="compose-ul"><ListBullets weight="bold" className="w-3.5 h-3.5" /></ToolbarButton>
                <ToolbarButton onClick={() => exec("insertOrderedList")} label="Numbered list" testId="compose-ol"><ListNumbers weight="bold" className="w-3.5 h-3.5" /></ToolbarButton>
                <span className="w-px h-4 bg-slate-200 mx-1" />
                <ToolbarButton onClick={handleLink} label="Insert link" testId="compose-link"><LinkSimple weight="bold" className="w-3.5 h-3.5" /></ToolbarButton>
              </div>
              <div
                ref={bodyRef}
                contentEditable
                suppressContentEditableWarning
                className={`${maximized ? "min-h-[48vh] max-h-[62vh]" : "min-h-[200px] max-h-[360px]"} overflow-y-auto p-3 text-sm focus:outline-none prose prose-sm max-w-none`}
                data-testid="compose-body"
              />
            </div>
          </div>

          {/* Attachments */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Label className="text-xs font-medium">Attachments</Label>
              <div className="flex items-center gap-1.5">
                {caseId && (
                  <Button type="button" variant="outline" size="sm" className="h-7 px-2 text-xs gap-1" onClick={() => setShowDocPicker((s) => !s)} data-testid="compose-attach-case-toggle">
                    <FileText weight="bold" className="w-3 h-3" /> Case docs
                  </Button>
                )}
                <Button type="button" variant="outline" size="sm" className="h-7 px-2 text-xs gap-1" onClick={() => fileInputRef.current?.click()} data-testid="compose-attach-upload-btn">
                  <Paperclip weight="bold" className="w-3 h-3" /> Upload file
                </Button>
                <input ref={fileInputRef} type="file" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="compose-file-input" />
              </div>
            </div>

            {showDocPicker && (
              <div className="border border-slate-200 rounded-md bg-slate-50" data-testid="compose-doc-picker">
                {caseDocs.length === 0 ? (
                  <p className="p-3 text-xs text-slate-400 text-center">No documents on this case yet.</p>
                ) : (
                  <>
                    <div className="p-2 border-b border-slate-200 bg-white">
                      <input
                        type="text"
                        value={docFilter}
                        onChange={(e) => setDocFilter(e.target.value)}
                        placeholder="Search documents…"
                        className="w-full text-xs px-2 py-1 border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        data-testid="compose-doc-filter"
                      />
                    </div>
                    <div className="max-h-72 overflow-y-auto">
                      {(() => {
                        const q = docFilter.trim().toLowerCase();
                        const matches = (d) => !q || (d.original_filename || "").toLowerCase().includes(q);
                        // Group by folder using folder_id → folder name lookup.
                        const folderName = (fid) => (caseFolders.find((f) => f.id === fid)?.name) || "Unfiled";
                        const groups = {};
                        caseDocs.filter(matches).forEach((d) => {
                          const name = folderName(d.folder_id);
                          (groups[name] = groups[name] || []).push(d);
                        });
                        const ordered = Object.keys(groups).sort((a, b) => {
                          if (a === "Unfiled") return 1;
                          if (b === "Unfiled") return -1;
                          return a.localeCompare(b);
                        });
                        if (ordered.length === 0) {
                          return <p className="p-3 text-xs text-slate-400 text-center">No matches.</p>;
                        }
                        return ordered.map((fname) => (
                          <div key={fname}>
                            <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500 bg-slate-100 border-b border-slate-200">
                              {fname} <span className="text-slate-400 normal-case font-normal">· {groups[fname].length}</span>
                            </div>
                            <ul className="divide-y divide-slate-200">
                              {groups[fname].map((d) => {
                                const checked = attachDocIds.includes(d.id);
                                const fname2 = d.original_filename || d.filename || "(unnamed)";
                                const sizeKb = ((d.size || d.file_size || 0) / 1024).toFixed(0);
                                return (
                                  <li key={d.id}>
                                    <label className="flex items-center gap-2 px-3 py-1.5 hover:bg-white cursor-pointer text-xs">
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={(e) => {
                                          setAttachDocIds((arr) => e.target.checked ? [...arr, d.id] : arr.filter((x) => x !== d.id));
                                        }}
                                        data-testid={`compose-doc-pick-${d.id}`}
                                      />
                                      <FileText weight="duotone" className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                                      <span className="truncate flex-1">{fname2}</span>
                                      <span className="text-slate-400 ml-auto shrink-0">{sizeKb} KB</span>
                                    </label>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        ));
                      })()}
                    </div>
                  </>
                )}
              </div>
            )}

            {(attachDocIds.length > 0 || uploads.length > 0) && (
              <div className="flex flex-wrap gap-1.5" data-testid="compose-attachments-summary">
                {attachDocIds.map((id) => {
                  const d = caseDocs.find((x) => x.id === id);
                  if (!d) return null;
                  return (
                    <span key={id} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs">
                      <FileText weight="bold" className="w-3 h-3" />
                      <span className="max-w-[160px] truncate">{d.original_filename || d.filename || "(unnamed)"}</span>
                      <button onClick={() => setAttachDocIds((a) => a.filter((x) => x !== id))} className="hover:text-blue-900" data-testid={`compose-remove-doc-${id}`}>
                        <X weight="bold" className="w-3 h-3" />
                      </button>
                    </span>
                  );
                })}
                {uploads.map((u, i) => (
                  <span key={`up-${i}`} className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs">
                    <Paperclip weight="bold" className="w-3 h-3" />
                    <span className="max-w-[160px] truncate">{u.filename}</span>
                    <button onClick={() => setUploads((a) => a.filter((_, j) => j !== i))} className="hover:text-emerald-900" data-testid={`compose-remove-upload-${i}`}>
                      <X weight="bold" className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {totalUploadKb > 0 && (
                  <span className="text-[10px] text-slate-400 self-center ml-1">~{totalUploadKb.toFixed(0)} KB uploads</span>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
          <Button
            onClick={handleSend}
            disabled={sending}
            className="text-white gap-1.5"
            style={{ background: "#100f5d" }}
            data-testid="compose-send-btn"
          >
            <ArrowSquareOut weight="bold" className="w-3.5 h-3.5" />
            {sending ? "Sending…" : (
              mode === "reply" ? "Send reply"
              : mode === "replyAll" ? "Send reply all"
              : mode === "forward" ? "Forward"
              : "Send email"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ToolbarButton({ onClick, children, label, testId }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}  // keep editor focus
      onClick={onClick}
      title={label}
      aria-label={label}
      className="p-1.5 rounded hover:bg-slate-200 text-slate-600 hover:text-slate-900 transition-colors"
      data-testid={testId}
    >
      {children}
    </button>
  );
}

/**
 * Compact "people on this case" picker. Renders a small icon button that
 * opens a popover with every address saved on the case (clients, parties,
 * referrer). Clicking an item toggles its inclusion in the target field
 * (To or Cc) — we preserve any addresses the user typed manually.
 */
function AddressPicker({ book, current, onChange, testId }) {
  const splitEmails = (s) => (s || "").split(/[,;\n]/).map((e) => e.trim()).filter(Boolean);
  const has = (email) => splitEmails(current).some((e) => e.toLowerCase() === email.toLowerCase());
  const toggle = (email) => {
    const parts = splitEmails(current);
    if (has(email)) {
      onChange(parts.filter((e) => e.toLowerCase() !== email.toLowerCase()).join(", "));
    } else {
      onChange([...parts, email].join(", "));
    }
  };
  if (!book || book.length === 0) {
    return null;
  }
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Add from people on this case"
          className="px-2.5 h-9 rounded-md border border-slate-200 bg-white text-slate-500 hover:text-slate-900 hover:bg-slate-50 flex items-center justify-center"
          data-testid={testId}
        >
          <AddressBook weight="bold" className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-1 max-h-80 overflow-y-auto">
        <p className="px-2 py-1.5 text-[10px] uppercase tracking-wide font-semibold text-slate-400">
          People on this case
        </p>
        <ul className="space-y-0.5">
          {book.map((p) => {
            const selected = has(p.email);
            return (
              <li key={p.email}>
                <button
                  type="button"
                  onClick={() => toggle(p.email)}
                  className={`w-full text-left px-2 py-1.5 rounded-md text-xs flex items-start gap-2 hover:bg-slate-50 ${selected ? "bg-blue-50/60" : ""}`}
                  data-testid={`${testId}-item-${p.email}`}
                >
                  <span className={`w-4 h-4 mt-0.5 rounded border flex items-center justify-center shrink-0 ${selected ? "bg-blue-600 border-blue-600 text-white" : "border-slate-300 text-transparent"}`}>
                    <Check weight="bold" className="w-3 h-3" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-medium text-slate-900 truncate">{p.name || p.email}</span>
                    <span className="block text-[11px] text-slate-500 truncate">{p.email}</span>
                    <span className="block text-[10px] text-slate-400 truncate">{p.role}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
